import { Button, Flex, Heading, Tooltip, VStack } from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchController } from "../../utils/FetchController/fetchController";
import Loader from "../../utils/Loader/Loader";
import PaginationSizeSelect from "../../utils/Loader/PaginationSizeSelect";
import { getHostDomain, toBoolean } from "../../utils/utils";
import { AgencyContext } from "../Settings/AgencyProvider/AgencyProvider";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import Table from "../Tables/Table";
import Paginator from "./../../utils/Loader/Paginator";
import FormsTour from "./FormsTour";
import { MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import ConfirmationModal from "../Modals/Confirmation";

export default function FormListPage() {
  const baseUrl = getHostDomain();
  const [status, setStatus] = useState("idle");
  const [tableColumns, setTableColumns] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [paginationSize, setPaginationSize] = useState(10);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  async function fetchAllForms() {
    setStatus("loading");
    const response = await fetchController(
      baseUrl + `/api/v1/form/list?size=${paginationSize}&page=${page}`
    );
    setPages(response?.data?.pages);
    setTableData(
      response.data.items.map((item) => ({
        name: item.name || "",
        formFields: item.fields,
        trigger_tools: item.trigger_tools,
        fields: item.fields.map((field) => field.label).join(", "),
        form_type: item.form_type,
        trigger_instructions: item.trigger_instructions || "",
        id: item.id,
      }))
    );
    const headsData = ["name", "fields", "form_type", "trigger_instructions"];
    const columns = headsData.map((key) => ({
      Header: key,
      accessor: key,
      Filter: ({ column }) => <input {...column.filterProps} />,
    }));
    setTableColumns(columns);
    setStatus("success");
  }

  const [formTourVisibility, setFormTourVisibility] = useState(
    localStorage.getItem("formTourVisibility") ? false : true
  );
  const onCloseTour = () => {
    localStorage.setItem("formTourVisibility", false);
    setFormTourVisibility(false);
  };
  const navigate = useNavigate();
  useEffect(() => {
    fetchAllForms();
  }, [page, paginationSize]);

  const { state: activePlan } = useContext(SettingContext);
  const agency = useContext(AgencyContext);
  const { textColor, buttonColorScheme, myDetails } = agency;

  const isAddFormBtnEnable = toBoolean(activePlan.credits?.form);
  const toolTipMsg =
    myDetails && myDetails?.agency_id
      ? `Please contact ${myDetails?.branding?.agency_name || ""} `
      : "Please upgrade to Gold Plan or above";

  // 🔹 Confirm delete handler
  const handleConfirmDelete = async () => {
    try {
      await fetchController(
        `${baseUrl}/api/v1/form/multiple`,
        "DELETE",
        Array.from(selectedItems)
      );
      toast.success("Deleted selected forms!");
      setSelectedItems(new Set());
      fetchAllForms();
    } catch (error) {
      toast.error("Failed to delete selected forms");
    } finally {
      setIsConfirmOpen(false);
    }
  };

  return (
    <VStack h="100%" w={"100%"} spacing={2} justifyContent={"space-between"} borderRadius="md">
      <Flex direction={"column"} gap={3} h={"95%"} w={"100%"}>
        <Flex gap={2} w={"100%"} alignItems="center" p={2}>
          <Heading color={textColor} fontSize="xl">
            Forms
          </Heading>
          <Tooltip label={!isAddFormBtnEnable && toolTipMsg}>
            <Button
              colorScheme={buttonColorScheme}
              onClick={() => navigate(`/forms/new`)}
              size={"sm"}
              isDisabled={!isAddFormBtnEnable}
            >
              {"Create Form"}
            </Button>
          </Tooltip>
          {formTourVisibility ? <FormsTour onCloseTour={onCloseTour} /> : null}
        </Flex>

        {selectedItems.size > 1 && (
          <Button
            colorScheme="red"
            size={"sm"}
            width="70px"
            ml="auto"
            onClick={() => setIsConfirmOpen(true)} // 🔹 open confirmation modal
          >
            <MdDelete color="white" /> ({selectedItems.size})
          </Button>
        )}

        {status === "loading" ? (
          <Loader />
        ) : (
          <Table
            fetch={fetchAllForms}
            table={"forms"}
            columns={tableColumns}
            data={tableData}
            enableRowSelection={true}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
        )}
      </Flex>

      <Flex justifyContent={"flex-end"} alignItems={"center"} marginBlock={2} gap={2} w={"100%"}>
        <PaginationSizeSelect
          paginationSize={paginationSize}
          setPaginationSize={setPaginationSize}
          tableLength={tableData.length}
        />
        <Paginator
          colorScheme={buttonColorScheme}
          onChangePage={setPage}
          page={page}
          pages={pages}
        />
      </Flex>
      <ConfirmationModal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        table="forms"
      />
    </VStack>
  );
}
